import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getCategoryReportByDateRange,
    getCategoryReportHourly,
    getCategoryReportTaxwise,
    getCategoryReportTopBottomSelling,
    formatDateForApi
} from '../../utilities/api';

export const CATEGORY_REPORT_TABS = {
    FULL: 0,
    HOURLY: 1,
    TAXWISE: 2,
    PERFORMANCE: 3
};

const buildPayload = ({ fromDate, toDate, branchId }) => {
    const resolvedBranchId = branchId || 'All';
    const payload = {
        from_date: fromDate ? formatDateForApi(fromDate) : '',
        to_date: toDate ? formatDateForApi(toDate) : '',
        branch_id: resolvedBranchId
    };
    if (String(resolvedBranchId).toLowerCase() === 'all') {
        const clientId = localStorage.getItem('clientid');
        if (clientId) payload.client_id = clientId;
    }
    return payload;
};

export const fetchCategoryFullReport = createAsyncThunk(
    'categoryWiseReports/fetchCategoryFullReport',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const res = await getCategoryReportByDateRange(buildPayload({ fromDate, toDate, branchId }));
            if (res?.error) {
                return rejectWithValue(res.error);
            }
            return {
                bills: res?.data || res?.bills || [],
                summary: res?.summary || {},
                status: res?.status
            };
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch category full report');
        }
    }
);

export const fetchCategoryHourlyReport = createAsyncThunk(
    'categoryWiseReports/fetchCategoryHourlyReport',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const res = await getCategoryReportHourly(buildPayload({ fromDate, toDate, branchId }));
            if (res?.error) return rejectWithValue(res.error);
            const flat = Array.isArray(res?.data) ? res.data : (res?.data && typeof res.data === 'object' ? null : null);
            if (flat) {
                const grouped = {};
                flat.forEach((row) => {
                    const cat = row.category_name || 'Unknown';
                    if (!grouped[cat]) grouped[cat] = [];
                    grouped[cat].push({
                        ...row,
                        Total_Hourly_Sales: row.total_amount,
                        Total_Hourly_qty: row.total_qty
                    });
                });
                return grouped;
            }
            return res?.data && typeof res.data === 'object' ? res.data : {};
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch category hourly report');
        }
    }
);

export const fetchCategoryTaxwiseReport = createAsyncThunk(
    'categoryWiseReports/fetchCategoryTaxwiseReport',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const res = await getCategoryReportTaxwise(buildPayload({ fromDate, toDate, branchId }));
            if (res?.error) return rejectWithValue(res.error);
            return Array.isArray(res?.data) ? res.data : [];
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch category tax-wise report');
        }
    }
);

export const fetchCategoryPerformanceReport = createAsyncThunk(
    'categoryWiseReports/fetchCategoryPerformanceReport',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const res = await getCategoryReportTopBottomSelling(buildPayload({ fromDate, toDate, branchId }));
            if (res?.error) return rejectWithValue(res.error);
            return {
                report_period: res?.report_period || null,
                top_selling: Array.isArray(res?.top_selling) ? res.top_selling : [],
                bottom_selling: Array.isArray(res?.bottom_selling) ? res.bottom_selling : []
            };
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch category performance report');
        }
    }
);

const initialState = {
    selectedBranch: null,
    fromDate: null,
    toDate: null,
    activeTab: CATEGORY_REPORT_TABS.FULL,

    fullReport: { bills: [], summary: {} },
    hourlyReport: {},
    taxwiseReport: [],
    performanceReport: { report_period: null, top_selling: [], bottom_selling: [] },

    loading: { full: false, hourly: false, taxwise: false, performance: false },
    error: { full: null, hourly: null, taxwise: null, performance: null },
    lastQuery: { full: null, hourly: null, taxwise: null, performance: null }
};

const categoryWiseReportsSlice = createSlice({
    name: 'categoryWiseReports',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.activeTab = CATEGORY_REPORT_TABS.FULL;

            state.fullReport = { bills: [], summary: {} };
            state.hourlyReport = {};
            state.taxwiseReport = [];
            state.performanceReport = { report_period: null, top_selling: [], bottom_selling: [] };

            state.loading = { full: false, hourly: false, taxwise: false, performance: false };
            state.error = { full: null, hourly: null, taxwise: null, performance: null };
            state.lastQuery = { full: null, hourly: null, taxwise: null, performance: null };
        },
        clearErrors: (state) => {
            state.error = { full: null, hourly: null, taxwise: null, performance: null };
        }
    },
    extraReducers: (builder) => {
        builder
            // Full
            .addCase(fetchCategoryFullReport.pending, (state) => {
                state.loading.full = true;
                state.error.full = null;
            })
            .addCase(fetchCategoryFullReport.fulfilled, (state, action) => {
                state.loading.full = false;
                state.fullReport = { bills: action.payload?.bills || [], summary: action.payload?.summary || {} };
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.full = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchCategoryFullReport.rejected, (state, action) => {
                state.loading.full = false;
                state.error.full = action.payload;
                state.fullReport = { bills: [], summary: {} };
            })
            // Hourly
            .addCase(fetchCategoryHourlyReport.pending, (state) => {
                state.loading.hourly = true;
                state.error.hourly = null;
            })
            .addCase(fetchCategoryHourlyReport.fulfilled, (state, action) => {
                state.loading.hourly = false;
                state.hourlyReport = action.payload != null && typeof action.payload === 'object' ? action.payload : {};
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.hourly = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchCategoryHourlyReport.rejected, (state, action) => {
                state.loading.hourly = false;
                state.error.hourly = action.payload;
                state.hourlyReport = {};
            })
            // Taxwise
            .addCase(fetchCategoryTaxwiseReport.pending, (state) => {
                state.loading.taxwise = true;
                state.error.taxwise = null;
            })
            .addCase(fetchCategoryTaxwiseReport.fulfilled, (state, action) => {
                state.loading.taxwise = false;
                state.taxwiseReport = Array.isArray(action.payload) ? action.payload : [];
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.taxwise = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchCategoryTaxwiseReport.rejected, (state, action) => {
                state.loading.taxwise = false;
                state.error.taxwise = action.payload;
                state.taxwiseReport = [];
            })
            // Performance
            .addCase(fetchCategoryPerformanceReport.pending, (state) => {
                state.loading.performance = true;
                state.error.performance = null;
            })
            .addCase(fetchCategoryPerformanceReport.fulfilled, (state, action) => {
                state.loading.performance = false;
                state.performanceReport = action.payload || { report_period: null, top_selling: [], bottom_selling: [] };
                const { fromDate, toDate, branchId } = action.meta.arg || {};
                state.lastQuery.performance = `${formatDateForApi(fromDate)}|${formatDateForApi(toDate)}|${branchId || 'All'}`;
            })
            .addCase(fetchCategoryPerformanceReport.rejected, (state, action) => {
                state.loading.performance = false;
                state.error.performance = action.payload;
                state.performanceReport = { report_period: null, top_selling: [], bottom_selling: [] };
            });
    }
});

export const { setFromDate, setToDate, setSelectedBranch, setActiveTab, resetFilters, clearErrors } =
    categoryWiseReportsSlice.actions;

export const selectCategoryWiseReports = (state) => state.categoryWiseReports;
export const selectActiveTab = (state) => state.categoryWiseReports.activeTab;
export const selectSelectedBranch = (state) => state.categoryWiseReports.selectedBranch;
export const selectFromDate = (state) => state.categoryWiseReports.fromDate;
export const selectToDate = (state) => state.categoryWiseReports.toDate;

export const selectFullReport = (state) => state.categoryWiseReports.fullReport;
export const selectHourlyReport = (state) => state.categoryWiseReports.hourlyReport;
export const selectTaxwiseReport = (state) => state.categoryWiseReports.taxwiseReport;
export const selectPerformanceReport = (state) => state.categoryWiseReports.performanceReport;
export const selectLastQuery = (state) => state.categoryWiseReports.lastQuery;

// Backward-compat selector name used by old Hourly UI
export const selectCategoryData = (state) => state.categoryWiseReports.hourlyReport;

export const selectActiveLoading = (state) => {
    const tab = state.categoryWiseReports.activeTab;
    const l = state.categoryWiseReports.loading;
    if (tab === CATEGORY_REPORT_TABS.FULL) return l.full;
    if (tab === CATEGORY_REPORT_TABS.HOURLY) return l.hourly;
    if (tab === CATEGORY_REPORT_TABS.TAXWISE) return l.taxwise;
    if (tab === CATEGORY_REPORT_TABS.PERFORMANCE) return l.performance;
    return false;
};

export const selectActiveError = (state) => {
    const tab = state.categoryWiseReports.activeTab;
    const e = state.categoryWiseReports.error;
    if (tab === CATEGORY_REPORT_TABS.FULL) return e.full;
    if (tab === CATEGORY_REPORT_TABS.HOURLY) return e.hourly;
    if (tab === CATEGORY_REPORT_TABS.TAXWISE) return e.taxwise;
    if (tab === CATEGORY_REPORT_TABS.PERFORMANCE) return e.performance;
    return null;
};

export default categoryWiseReportsSlice.reducer;
